// Die Regeln der Fälle-Liste: Wie aus Fällen, Bewegungen und Terminen die
// Zeilen und Gruppen werden — nach Datum oder nach Titel.
//
// REINE FUNKTIONEN ohne Netz, wie bei der Übersicht: Der Haken holt, die
// Vorlage übergibt Beispiele, beide sehen dieselben Regeln.

import type { CaseMovement, ResearchCase, UpcomingCatalyst } from '../api/client'
import { label } from '../pages/researchVocabulary'

export type Ereignis = 'Neuer Fall' | 'Update' | 'Wahrsch. geändert'
  | 'Wirkung geändert' | 'Beendet' | 'Termin'

export type Ton = 'accent' | 'muted' | 'bad' | 'good' | 'warn' | 'dim'

/** Eine Zeile der Liste — eine Bewegung, ein Termin oder ein Fall selbst. */
export interface FallZeile {
  key: string
  fallId: number
  ereignis: Ereignis
  ereignisTon: Ton
  /** Die Art des Falls in Worten. */
  art: string
  /** Der erste betroffene Titel, „+n" für weitere. */
  titelKurz: string
  /** Die Überschrift der Bewegung — null bei Fall und Erstellung. */
  kopf: string | null
  /** Der Titel des Falls. */
  vorgang: string
  neu: boolean
  /** Der Zeitpunkt der Zeile, ISO — zum Ordnen und Gruppieren. */
  zeit: string
  /** Rechts in der Kopfzeile: Uhrzeit oder Tag. */
  rechts: string
  /** Beim Überfahren: ausgeschrieben. */
  rechtsVoll: string
  termin: { bestaetigt: boolean; wesentlichkeit: number | null; inText: string } | null
  kpi: { wirkung: number; trend: string; deltaTon: 'good' | 'bad' | 'neutral';
         last: number | null; hinweis: string } | null
}

export interface FallGruppe {
  key: string
  /** „Heute", „Mo 08.09.26" oder der Titel. */
  label: string
  /** „Freitag, 06.09.26", „geplant", „3 Fälle". */
  sub: string
  /** „4 Bewegungen · 1 neu", „Summe Wirkung 9 · offen 2". */
  rechts: string
  zeilen: FallZeile[]
}

const WOCHENTAG_KURZ = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const WOCHENTAG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag',
                   'Freitag', 'Samstag']
const MONAT = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli',
               'August', 'September', 'Oktober', 'November', 'Dezember']

const zwei = (n: number) => String(n).padStart(2, '0')

/** JJJJ-MM-TT in Ortszeit aus einem Zeitpunkt oder einem Tag. */
export function tagVon(iso: string): string {
  const d = iso.length <= 10 ? new Date(`${iso}T12:00:00`) : new Date(iso)
  return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`
}

/** 04.09.26 */
export function tagKurz(iso: string | null): string {
  if (!iso) return '—'
  const t = tagVon(iso)
  return `${t.slice(8, 10)}.${t.slice(5, 7)}.${t.slice(2, 4)}`
}

/** 17:20 */
export function uhr(iso: string): string {
  const d = new Date(iso)
  return `${zwei(d.getHours())}:${zwei(d.getMinutes())}`
}

/** Freitag, 4. September 2026, 17:20 */
export function voll(iso: string, mitUhr = true): string {
  const d = iso.length <= 10 ? new Date(`${iso}T12:00:00`) : new Date(iso)
  const tag = `${WOCHENTAG[d.getDay()]}, ${d.getDate()}. ${MONAT[d.getMonth()]} ${d.getFullYear()}`
  return mitUhr && iso.length > 10 ? `${tag}, ${uhr(iso)}` : tag
}

function tageZwischen(a: string, b: string): number {
  return Math.round((new Date(`${b}T12:00:00`).getTime() - new Date(`${a}T12:00:00`).getTime()) / 86_400_000)
}

/** Die Farbe der Wirkung: 4 rot, 2–3 orange, 1 grün, 0 grau. */
export function wirkungTon(w: number): Ton {
  return w >= 4 ? 'bad' : w >= 2 ? 'warn' : w === 1 ? 'good' : 'dim'
}

/** Die Farbe des Stands. */
export function standTon(status: string): Ton {
  if (status === 'ACTIVE') return 'accent'
  if (status === 'RESOLVED_POSITIVE') return 'good'
  if (status === 'RESOLVED_NEGATIVE') return 'bad'
  if (status === 'SETTLEMENT' || status === 'PENDING_DECISION' || status === 'APPEAL') return 'warn'
  return 'muted'
}

function titelKurz(instrumente: { name: string }[]): string {
  if (instrumente.length === 0) return 'ohne Titel'
  const [erster, ...rest] = instrumente
  return rest.length ? `${erster.name} +${rest.length}` : erster.name
}

function prozent(wert: string | null): string {
  return wert === null ? '—' : `${Number(wert).toFixed(0)} %`
}

/** Was eine Bewegung ist — aus Art und Inhalt des Eintrags. */
export function ereignisAus(u: CaseMovement): { ereignis: Ereignis; ton: Ton } {
  if (u.update_type === 'NEW_CASE') return { ereignis: 'Neuer Fall', ton: 'accent' }
  if (u.update_type === 'FINAL_RESOLUTION' || u.update_type === 'CASE_DISMISSED') {
    return { ereignis: 'Beendet', ton: 'good' }
  }
  if (u.update_type === 'ASSESSMENT_CHANGED') return { ereignis: 'Wirkung geändert', ton: 'bad' }
  if (u.probability_before !== null && u.probability_after !== null
      && Number(u.probability_before) !== Number(u.probability_after)) {
    return { ereignis: 'Wahrsch. geändert', ton: 'muted' }
  }
  return { ereignis: 'Update', ton: 'muted' }
}

function kpiAusFall(f: Pick<ResearchCase, 'regime_impact' | 'probability' | 'overhang_score'>): FallZeile['kpi'] {
  return {
    wirkung: f.regime_impact, trend: prozent(f.probability), deltaTon: 'neutral',
    last: f.overhang_score,
    hinweis: f.probability === null ? 'ohne Wahrsch.' : 'unverändert',
  }
}

function kpiAusBewegung(u: CaseMovement): FallZeile['kpi'] {
  const b = u.probability_before, a = u.probability_after
  if (b !== null && a !== null && Number(b) !== Number(a)) {
    return {
      wirkung: u.regime_impact, trend: `${Number(b).toFixed(0)} → ${Number(a).toFixed(0)} %`,
      deltaTon: Number(a) > Number(b) ? 'bad' : 'good', last: u.overhang_score,
      hinweis: 'mit dieser Bewegung',
    }
  }
  return kpiAusFall({ regime_impact: u.regime_impact, probability: u.probability,
                      overhang_score: u.overhang_score })
}

// --------------------------------------------------------------- Zeilen

/** Der Fall selbst als Zeile — für die Sicht nach Titel. */
export function zeileAusFall(f: ResearchCase): FallZeile {
  const bewegt = f.last_material_update_at ?? f.updated ?? f.created
  return {
    key: `fall-${f.id}`, fallId: f.id,
    ereignis: f.last_material_update_at ? 'Update' : 'Neuer Fall',
    ereignisTon: f.last_material_update_at ? 'muted' : 'accent',
    art: label('case_type', f.case_type), titelKurz: titelKurz(f.instruments),
    kopf: null, vorgang: f.title, neu: false, zeit: bewegt,
    rechts: tagKurz(bewegt),
    rechtsVoll: `Erstellt ${voll(f.created)} · zuletzt bewegt ${voll(bewegt)}`,
    termin: null, kpi: kpiAusFall(f),
  }
}

/** Die Erstellung eines Falls als Zeile — für die Sicht nach Datum. */
export function zeileAusErstellung(f: ResearchCase, seit: Date): FallZeile {
  return {
    key: `erstellt-${f.id}`, fallId: f.id, ereignis: 'Neuer Fall', ereignisTon: 'accent',
    art: label('case_type', f.case_type), titelKurz: titelKurz(f.instruments),
    kopf: null, vorgang: f.title, neu: new Date(f.created) >= seit, zeit: f.created,
    rechts: uhr(f.created), rechtsVoll: `Neuer Fall ${voll(f.created)}`,
    termin: null, kpi: kpiAusFall(f),
  }
}

/** Eine Bewegung als Zeile. */
export function zeileAusBewegung(u: CaseMovement, seit: Date): FallZeile {
  const { ereignis, ton } = ereignisAus(u)
  return {
    key: `bewegung-${u.id}`, fallId: u.case_id, ereignis, ereignisTon: ton,
    art: label('case_type', u.case_type), titelKurz: titelKurz(u.instruments),
    kopf: u.headline, vorgang: u.case_title, neu: new Date(u.recorded_at) >= seit,
    zeit: u.recorded_at, rechts: uhr(u.recorded_at),
    rechtsVoll: `${ereignis} ${voll(u.recorded_at)}`,
    termin: null, kpi: kpiAusBewegung(u),
  }
}

/** Ein anstehender Termin als Zeile. */
export function zeileAusTermin(k: UpcomingCatalyst, heute: string): FallZeile | null {
  if (!k.date_from || k.case_id === null) return null
  const tage = tageZwischen(heute, k.date_from)
  return {
    key: `termin-${k.id}`, fallId: k.case_id, ereignis: 'Termin', ereignisTon: 'accent',
    art: label('catalyst_type', k.catalyst_type), titelKurz: k.instrument_name,
    kopf: k.title, vorgang: k.case_title ?? '', neu: false,
    zeit: `${k.date_from}T12:00:00`, rechts: tagKurz(k.date_from),
    rechtsVoll: `Termin ${voll(k.date_from, false)}`,
    termin: { bestaetigt: k.confirmed, wesentlichkeit: k.expected_materiality,
              inText: tage === 0 ? 'heute' : tage === 1 ? 'morgen' : `in ${tage} Tagen` },
    kpi: null,
  }
}

// -------------------------------------------------------------- Gruppen

/** „Heute", „Gestern", „Morgen" oder „Mo 08.09.26". */
export function tagLabel(tag: string, heute: string): string {
  const d = tageZwischen(heute, tag)
  if (d === 0) return 'Heute'
  if (d === -1) return 'Gestern'
  if (d === 1) return 'Morgen'
  return `${WOCHENTAG_KURZ[new Date(`${tag}T12:00:00`).getDay()]} ${tagKurz(tag)}`
}

/**
 * Nach Datum: jede Bewegung eine Zeile, Neues oben, Termine in der Zukunft
 * darüber als „geplant".
 */
export function nachDatum(faelle: ResearchCase[], bewegungen: CaseMovement[],
                          termine: UpcomingCatalyst[], heute: string,
                          seit: Date): FallGruppe[] {
  const bekannt = new Set(faelle.map((f) => f.id))
  const zeilen: FallZeile[] = [
    ...faelle.map((f) => zeileAusErstellung(f, seit)),
    ...bewegungen.filter((u) => bekannt.has(u.case_id)).map((u) => zeileAusBewegung(u, seit)),
    ...termine.map((k) => zeileAusTermin(k, heute))
      .filter((z): z is FallZeile => z !== null && bekannt.has(z.fallId)),
  ].sort((a, b) => b.zeit.localeCompare(a.zeit))

  const tage = new Map<string, FallZeile[]>()
  for (const z of zeilen) {
    const tag = tagVon(z.zeit)
    tage.set(tag, [...(tage.get(tag) ?? []), z])
  }
  return [...tage.entries()].map(([tag, eigene]) => {
    const zukunft = tag > heute
    const termineN = eigene.filter((z) => z.ereignis === 'Termin').length
    const neu = eigene.filter((z) => z.neu).length
    const d = new Date(`${tag}T12:00:00`)
    const nah = Math.abs(tageZwischen(heute, tag)) <= 1
    return {
      key: tag, label: tagLabel(tag, heute),
      sub: `${WOCHENTAG[d.getDay()]}${nah ? `, ${tagKurz(tag)}` : ''}${zukunft ? ' · geplant' : ''}`,
      rechts: zukunft
        ? `${termineN} ${termineN === 1 ? 'Termin' : 'Termine'}`
        : `${eigene.length} ${eigene.length === 1 ? 'Bewegung' : 'Bewegungen'}${neu ? ` · ${neu} neu` : ''}`,
      zeilen: eigene,
    }
  })
}

/** Nach Titel: die Fälle je erstem Titel, schwerste Gruppe zuerst. */
export function nachTitel(faelle: ResearchCase[]): FallGruppe[] {
  const titel = new Map<string, ResearchCase[]>()
  for (const f of faelle) {
    const name = f.instruments[0]?.name ?? 'ohne Titel'
    titel.set(name, [...(titel.get(name) ?? []), f])
  }
  return [...titel.entries()]
    .map(([name, eigene]) => ({ name, eigene, summe: eigene.reduce((s, f) => s + f.regime_impact, 0) }))
    .sort((a, b) => b.summe - a.summe || a.name.localeCompare(b.name))
    .map(({ name, eigene, summe }) => ({
      key: name, label: name,
      sub: `${eigene.length} ${eigene.length === 1 ? 'Fall' : 'Fälle'}`,
      rechts: `Summe Wirkung ${summe} · offen ${eigene.filter((f) => f.active).length}`,
      zeilen: [...eigene].sort((a, b) => b.regime_impact - a.regime_impact).map(zeileAusFall),
    }))
}

/** Die Reihenfolge fürs Blättern: jeder Fall einmal, in Listenfolge. */
export function reiheAus(gruppen: FallGruppe[]): number[] {
  return [...new Set(gruppen.flatMap((g) => g.zeilen.map((z) => z.fallId)))]
}
